import { RefObject, useEffect, useState } from 'react';

export type UseInViewOptions = {
    /** e.g. `'200px 0px'` — start loading before the block enters the viewport */
    rootMargin?: string;
    threshold?: number | number[];
    /** When true (default), `inView` stays true after the first intersection */
    triggerOnce?: boolean;
};

/**
 * Observes when a DOM node intersects the viewport. Useful to defer mounting
 * children (and their data-fetching hooks) until the user scrolls near them.
 */
export function useInView<T extends Element>(
    ref: RefObject<T>,
    { rootMargin = '0px', threshold = 0, triggerOnce = true }: UseInViewOptions = {},
): boolean {
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) {
            return undefined;
        }

        const observer = new IntersectionObserver(
            entries => {
                const entry = entries[0];
                if (!entry) {
                    return;
                }
                if (entry.isIntersecting) {
                    setInView(true);
                    if (triggerOnce) {
                        observer.disconnect();
                    }
                } else if (!triggerOnce) {
                    setInView(false);
                }
            },
            { rootMargin, threshold },
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [ref, rootMargin, threshold, triggerOnce]);

    return inView;
}
